import { ResponseAddress } from 'shared/interface/globalInterface';
import * as postcodeApi from 'utils/api/idealPostcode';
import { Nullable } from 'utils/types';
import { AddressType, FIELDS } from 'components/Forms/Address/types';
import { Address } from 'components/DetailsPageBoilerplate/types';
import { OldAddress, ResponseOldAddress } from 'domain/Address';
import { isNotEmptyString } from 'utils/form/formUtils';
import { mappedAddress } from 'components/AdviserDetails/interface/AdviserDetailsInterface';

export const mapAddress = (address: ResponseAddress): AddressType => ({
  flatNumber: address.flatNumber,
  buildingName: address.buildingName,
  buildingNumber: address.buildingNumber,
  street: address.street,
  city: address.city,
  county: address.county,
  postcode: address.postcode,
});

export const mapOldAddress = (address: ResponseOldAddress): OldAddress => ({
  houseNumber: address.house_number,
  addressLine1: address.street_line_1 || '',
  addressLine2: address.street_line_2 || '',
  city: address.city,
  county: address.county,
  postcode: address.post_code,
});

export const mapOldAddressToNewAddress = (address: ResponseOldAddress): mappedAddress => ({
  flatNumber: '',
  buildingName: address.street_line_1 || '',
  buildingNumber: address.house_number || '',
  street: address.street_line_2 || '',
  city: address.city,
  county: address.county,
  postcode: address.post_code,
});

export const mapAddressToResponse = (address: AddressType): ResponseAddress => ({
  postcode: address.postcode,
  flatNumber: address.flatNumber,
  buildingName: address.buildingName,
  buildingNumber: address.buildingNumber,
  city: address.city,
  street: address.street,
  county: address.county,
});

export const formatFullAddress = (address: AddressType): string =>
  [address.flatNumber, address.buildingNumber, address.buildingName, address.street, address.city, address.postcode]
    .filter((item: any) => !!item)
    .join();

export const formatFullAddressOld = (address: Address): string =>
  [
    address.houseNumber,
    address.addressLine1 ? `, ${address.addressLine1}` : '',
    address.addressLine2 ? `, ${address.addressLine2}` : '',
    address.city ? `, ${address.city}` : '',
    address.postcode ? `, ${address.postcode}` : '',
  ].join(' ');

export const fetchAddresses = (postcode: string): Promise<AddressType[]> => {
  return postcodeApi
    .fetchAddresses(postcode)
    .then((data) => {
      return data.map((address) =>
        createAddress({
          flatNumber: address.flatNumber,
          buildingName: address.buildingName,
          buildingNumber: address.buildingNumber,
          street: address.street,
          city: address.city,
          postcode: address.postcode,
          county: address.county,
        }),
      );
    })
    .catch(() => {
      return [];
    });
};

type AddressInput = {
  [FIELDS.POSTCODE]?: Nullable<string>;
  [FIELDS.FLAT_NUMBER]?: Nullable<string>;
  [FIELDS.BUILDING_NAME]?: Nullable<string>;
  [FIELDS.BUILDING_NUMBER]?: Nullable<string>;
  [FIELDS.STREET]?: Nullable<string>;
  [FIELDS.CITY]?: Nullable<string>;
  [FIELDS.COUNTY]?: Nullable<string>;
};

export const createAddress = ({
  flatNumber = '',
  buildingName = '',
  buildingNumber = '',
  county = '',
  street = '',
  postcode = '',
  city = '',
}: AddressInput): AddressType => ({
  flatNumber,
  buildingName,
  buildingNumber,
  street,
  county,
  city,
  postcode,
});

export const formatAddress = (address: AddressType): string => {
  const firstPart = [address.flatNumber, address.buildingName, address.buildingNumber, address.street]
    .filter(isNotEmptyString)
    .join(' ');
  return [firstPart, address.city].filter(isNotEmptyString).join(', ');
};

export const formatAddressLabel = (address: AddressType): string => {
  const firstPart = [address.flatNumber, address.buildingName, address.buildingNumber]
    .filter(isNotEmptyString)
    .join(' ');
  return [firstPart, address.street, address.city, address.postcode].filter(isNotEmptyString).join(', ');
};

export const formatPostcode = (postcode: Nullable<string>): string => {
  if (!postcode) return '';

  const splittedValue = postcode.replace(' ', '').split('');
  const indexToSpace = splittedValue.length - 3;

  splittedValue.splice(indexToSpace, 0, ' ');

  return splittedValue.join('');
};

export const formatAdressInline = (address: AddressType): string => {
  return [
    address.flatNumber,
    address.buildingName,
    address.buildingNumber,
    address.city,
    formatPostcode(address[FIELDS.POSTCODE]),
  ]
    .filter((_) => _)
    .join(', ');
};
